import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Container,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AppsIcon from '@mui/icons-material/Apps';
import YouTubeIcon from '@mui/icons-material/YouTube';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import LanguageIcon from '@mui/icons-material/Language';
import { useQuery } from '@tanstack/react-query';
import PodscopePublicHeader from '../components/PodscopePublicHeader';
import TikTokIcon from '../components/icons/TikTokIcon';
import {
  fetchPublicGoogleSearch,
  type GoogleSearchFeedItem,
  type GoogleSearchFeedPlatform,
  type GoogleSearchFeedPlatformFilter,
  type GoogleSearchFeedResponse,
} from '../api/publicGoogleSearch';
import { formatChileanDateTime } from '../utils/dateUtils';

const NAVBAR_OFFSET = { xs: 10, sm: 11, md: 12 } as const;
const FEED_SLUG = 'papi-ricky';
const PAGE_SIZE = 20;
const REFETCH_MS = 3_600_000;

const PLATFORM_LABELS: Record<GoogleSearchFeedPlatform, string> = {
  google: 'Google News',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  instagram: 'Instagram',
  facebook: 'Facebook',
  other: 'Otro',
};

const FEED_PLATFORMS: GoogleSearchFeedPlatform[] = [
  'google',
  'youtube',
  'instagram',
  'tiktok',
  'facebook',
  'other',
];

function PlatformIcon({
  platform,
  fontSize = 16,
}: {
  platform: GoogleSearchFeedPlatform | 'all';
  fontSize?: number;
}) {
  const sx = { fontSize };

  if (platform === 'all') {
    return <AppsIcon sx={sx} />;
  }

  switch (platform) {
    case 'youtube':
      return <YouTubeIcon sx={{ ...sx, color: '#FF0000' }} />;
    case 'instagram':
      return <InstagramIcon sx={{ ...sx, color: '#E4405F' }} />;
    case 'facebook':
      return <FacebookIcon sx={{ ...sx, color: '#1877F2' }} />;
    case 'tiktok':
      return <TikTokIcon sx={sx} />;
    case 'google':
      return <NewspaperIcon sx={sx} />;
    default:
      return <LanguageIcon sx={sx} />;
  }
}

function PlatformMenuLabel({
  platform,
  count,
}: {
  platform: GoogleSearchFeedPlatform | 'all';
  count: number;
}) {
  const label = platform === 'all' ? 'Todas' : PLATFORM_LABELS[platform];

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <PlatformIcon platform={platform} />
      <span>
        {label} ({count})
      </span>
    </Stack>
  );
}

function Thumbnail({ item }: { item: GoogleSearchFeedItem }) {
  const [failed, setFailed] = useState(false);

  if (!item.thumbnailUrl || failed) {
    return (
      <Box
        sx={{
          width: { xs: '100%', sm: 200 },
          aspectRatio: '16 / 9',
          borderRadius: 2,
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <PlayCircleOutlineIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={item.thumbnailUrl}
      alt=""
      onError={() => setFailed(true)}
      sx={{
        width: { xs: '100%', sm: 200 },
        aspectRatio: '16 / 9',
        objectFit: 'cover',
        borderRadius: 2,
        flexShrink: 0,
        bgcolor: 'action.hover',
      }}
    />
  );
}

function FeedCard({ item }: { item: GoogleSearchFeedItem }) {
  return (
    <Card
      variant="outlined"
      sx={{
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: 1,
        },
      }}
    >
      <CardActionArea
        component="a"
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ p: { xs: 1.5, sm: 2 } }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
          <Thumbnail item={item} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip
                size="small"
                icon={<PlatformIcon platform={item.platform} />}
                label={PLATFORM_LABELS[item.platform]}
                variant="outlined"
                sx={{
                  pl: 0.75,
                  '& .MuiChip-icon': {
                    ml: 0.25,
                    mr: 0,
                    color: 'unset',
                  },
                  '& .MuiChip-label': {
                    pl: 0.5,
                  },
                }}
              />
              {item.source ? (
                <Chip size="small" label={item.source} variant="outlined" />
              ) : null}
            </Stack>

            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontFamily: '"DM Serif Display", serif',
                fontSize: { xs: '1.05rem', sm: '1.15rem' },
                lineHeight: 1.3,
                mb: 0.75,
              }}
            >
              {item.title}
            </Typography>

            {item.snippet ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {item.snippet}
              </Typography>
            ) : null}

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              {item.firstSeenAt ? (
                <Typography variant="caption" color="text.secondary">
                  Detectado {formatChileanDateTime(item.firstSeenAt)}
                </Typography>
              ) : null}
              {item.publishedText ? (
                <Typography variant="caption" color="text.secondary">
                  {item.publishedText}
                </Typography>
              ) : null}
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1 }}>
              <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {item.url}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <Stack spacing={2}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Card key={index} variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Skeleton variant="rounded" sx={{ width: { xs: '100%', sm: 200 }, aspectRatio: '16/9' }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="30%" />
              <Skeleton width="90%" height={32} sx={{ mt: 1 }} />
              <Skeleton width="60%" />
            </Box>
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}

function mergeItems(
  existing: GoogleSearchFeedItem[],
  incoming: GoogleSearchFeedItem[],
): GoogleSearchFeedItem[] {
  const seen = new Set(existing.map((item) => item.id));
  const appended = incoming.filter((item) => !seen.has(item.id));
  return [...existing, ...appended];
}

const PapiRickyGoogleSearch: React.FC = () => {
  const [platformFilter, setPlatformFilter] = useState<GoogleSearchFeedPlatformFilter>('all');
  const [items, setItems] = useState<GoogleSearchFeedItem[]>([]);
  const [meta, setMeta] = useState<Pick<
    GoogleSearchFeedResponse,
    'platformCounts' | 'sectionCounts' | 'lastUpdatedAt' | 'total' | 'hasMore' | 'page'
  > | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const appendedPastFirstPageRef = useRef(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['publicGoogleSearch', FEED_SLUG, platformFilter],
    queryFn: () => fetchPublicGoogleSearch(FEED_SLUG, 'all', platformFilter, 1, PAGE_SIZE),
    refetchInterval: REFETCH_MS,
  });

  useEffect(() => {
    if (!data) return;
    if (data.platform !== platformFilter) return;

    if (appendedPastFirstPageRef.current) {
      setMeta((prev) =>
        prev
          ? {
              ...prev,
              platformCounts: data.platformCounts,
              sectionCounts: data.sectionCounts,
              lastUpdatedAt: data.lastUpdatedAt,
              total: data.total,
            }
          : {
              platformCounts: data.platformCounts,
              sectionCounts: data.sectionCounts,
              lastUpdatedAt: data.lastUpdatedAt,
              total: data.total,
              hasMore: data.hasMore,
              page: data.page,
            },
      );
      return;
    }

    setItems(data.items);
    setMeta({
      platformCounts: data.platformCounts,
      sectionCounts: data.sectionCounts,
      lastUpdatedAt: data.lastUpdatedAt,
      total: data.total,
      hasMore: data.hasMore,
      page: data.page,
    });
    setLoadMoreError(null);
  }, [data, platformFilter]);

  const sectionCounts = meta?.sectionCounts ?? {
    all: 0,
    videos: 0,
    short_videos: 0,
    news: 0,
  };
  const platformCounts = meta?.platformCounts;
  const hasMore = meta?.hasMore ?? false;
  const currentPage = meta?.page ?? 1;
  const remainingCount = Math.max(0, (meta?.total ?? 0) - items.length);
  const showInitialLoading = isLoading && items.length === 0;

  const handlePlatformChange = (event: SelectChangeEvent<GoogleSearchFeedPlatformFilter>) => {
    appendedPastFirstPageRef.current = false;
    setPlatformFilter(event.target.value as GoogleSearchFeedPlatformFilter);
    setItems([]);
    setMeta(null);
    setLoadMoreError(null);
  };

  const handleLoadMore = async () => {
    if (loadingMore || isFetching || !hasMore) return;

    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const next = await fetchPublicGoogleSearch(
        FEED_SLUG,
        'all',
        platformFilter,
        currentPage + 1,
        PAGE_SIZE,
      );
      appendedPastFirstPageRef.current = true;
      setItems((prev) => mergeItems(prev, next.items));
      setMeta({
        platformCounts: next.platformCounts,
        sectionCounts: next.sectionCounts,
        lastUpdatedAt: next.lastUpdatedAt,
        total: next.total,
        hasMore: next.hasMore,
        page: next.page,
      });
    } catch (err) {
      setLoadMoreError(axiosErrorMessage(err));
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <PodscopePublicHeader maxWidth="lg" px={{ xs: 2, sm: 3 }} />
      <Container maxWidth="lg" sx={{ mt: NAVBAR_OFFSET, pb: 6, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"DM Serif Display", serif',
              fontSize: { xs: '2rem', sm: '2.5rem' },
              mb: 0.5,
            }}
          >
            Papi Ricky
          </Typography>
          {meta?.lastUpdatedAt ? (
            <Typography variant="caption" color="text.secondary">
              Última actualización {formatChileanDateTime(meta.lastUpdatedAt)}
              {isFetching && !showInitialLoading ? ' · actualizando…' : ''}
            </Typography>
          ) : null}
        </Box>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="papi-ricky-platform-label">Plataforma</InputLabel>
          <Select
            labelId="papi-ricky-platform-label"
            id="papi-ricky-platform"
            value={platformFilter}
            label="Plataforma"
            onChange={handlePlatformChange}
            renderValue={(value) => (
              <PlatformMenuLabel
                platform={value}
                count={
                  value === 'all'
                    ? sectionCounts.all
                    : platformCounts?.[value] ?? 0
                }
              />
            )}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 320 },
              },
            }}
          >
            <MenuItem value="all">
              <PlatformMenuLabel platform="all" count={sectionCounts.all} />
            </MenuItem>
            {FEED_PLATFORMS.map((platform) => (
              <MenuItem key={platform} value={platform}>
                <PlatformMenuLabel
                  platform={platform}
                  count={platformCounts?.[platform] ?? 0}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {showInitialLoading ? <LoadingSkeleton /> : null}

        {isError && items.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="error" sx={{ mb: 2 }}>
              {axiosErrorMessage(error)}
            </Typography>
            <MuiLink component="button" type="button" onClick={() => refetch()} sx={{ cursor: 'pointer' }}>
              Reintentar
            </MuiLink>
          </Box>
        ) : null}

        {!showInitialLoading && !isError && items.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Aún no hay resultados — el poller puede no haber corrido.
            </Typography>
          </Box>
        ) : null}

        {items.length > 0 ? (
          <Stack spacing={2}>
            {items.map((item) => (
              <FeedCard key={item.id} item={item} />
            ))}

            {loadMoreError ? (
              <Typography color="error" variant="body2" textAlign="center">
                {loadMoreError}
              </Typography>
            ) : null}

            {hasMore ? (
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  void handleLoadMore();
                }}
                disabled={loadingMore || isFetching}
                sx={{ mt: 1, py: 1.25 }}
              >
                {loadingMore
                  ? 'Cargando…'
                  : `Cargar más (${remainingCount})`}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Container>
    </>
  );
};

function axiosErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string } } }).response;
    if (response?.data?.error) return response.data.error;
  }
  if (error instanceof Error) return error.message;
  return 'No se pudo cargar el feed';
}

export default PapiRickyGoogleSearch;
